import { FC, memo, ReactNode, useEffect, CSSProperties, useMemo } from 'react';
import { XIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import TouchableOpacity from '@components/buttons/TouchableOpacity';
import SkewedButton from '@components/buttons/SkewedButton';
import { PremierText } from '@components/text';
import { SKEW_ANGLE, MOBILE_SKEW_ANGLE } from '@utils/constants';
import { useScreenSizes } from '@hooks/useScreenSizes';

const Container = styled.div`
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding-inline: 24px;
  background-color: ${({ theme }) =>
    `${theme.colors.black}${theme.opacity[60]}`};
  z-index: 1000;
`;

const ModalContainer = styled.div<{ $skewedAngle: number }>`
  width: 40%;
  max-width: 600px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  background: ${({ theme }) =>
    `linear-gradient(0deg, ${theme.colors.black} 10%, ${theme.colors.grey[600]} 80%)`};
  border: 1px solid ${({ theme }) => theme.colors.grey[600]};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transform: skewX(${({ $skewedAngle }) => -$skewedAngle}deg);

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 60%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 90%;
  }
`;

const ModalHeader = styled.div<{ $hasTitle?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: ${({ $hasTitle }) =>
    $hasTitle ? 'space-between' : 'flex-end'};
  align-items: center;
  margin-bottom: 8px;
`;

const ModalTitle = styled(PremierText)<{ $skewedAngle: number }>`
  font-size: ${({ theme }) => theme.fonts.fontSize.xl};
  color: ${({ theme }) => theme.colors.grey[200]};
  transform: skewX(${({ $skewedAngle }) => $skewedAngle}deg);
`;

const CloseButton = styled(XIcon)<{ $skewedAngle: number }>`
  color: ${({ theme }) => theme.colors.grey[200]};
  cursor: pointer;
  transition: color 0.2s;
  transform: skewX(${({ $skewedAngle }) => $skewedAngle}deg);

  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const ModalBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  margin: 8px 0;
  overflow: hidden;
`;

const ModalFooter = styled.div<{ $skewedAngle: number }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 16px;
  margin-top: 8px;
  transform: skewX(${({ $skewedAngle }) => $skewedAngle}deg);
`;

const FooterButton = styled(TouchableOpacity)`
  padding-inline: 16px;
  color: ${({ theme }) => theme.colors.grey[200]};
  font-family: ${({ theme }) => theme.fonts.fontFamily.leagueGothic};
  font-size: ${({ theme }) => theme.fonts.fontSize.base};
  font-style: italic;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.colors.white};
  text-transform: uppercase;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const ConfirmButton = styled(SkewedButton)`
  padding-inline: 16px;
  &:hover {
    opacity: 0.8;
  }
`;

type Props = {
  title?: string;
  children?: ReactNode;
  isShown: boolean;
  isCloseable?: boolean;
  isActionable?: boolean;
  isSkewed?: boolean;
  onConfirm?: () => void;
  onClose: () => void;
  style?: CSSProperties;
};

const Modal: FC<Props> = ({
  title,
  children,
  isShown,
  isCloseable = true,
  isActionable = true,
  isSkewed = true,
  onConfirm,
  onClose,
  style,
}) => {
  const { isMobile } = useScreenSizes();
  const skewedAngle = useMemo(
    () => (isSkewed ? (isMobile ? MOBILE_SKEW_ANGLE : SKEW_ANGLE) : 0),
    [isSkewed, isMobile],
  );

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (isShown && isCloseable && event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    if (isShown) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'auto';
    };
  }, [isShown, isCloseable, onClose]);

  if (!isShown) {
    return null;
  }

  return createPortal(
    <Container
      onClick={(e) => isCloseable && e.target === e.currentTarget && onClose()}
    >
      <ModalContainer $skewedAngle={skewedAngle} style={style}>
        <ModalHeader $hasTitle={!!title}>
          {!!title && (
            <ModalTitle $skewedAngle={skewedAngle}>{title}</ModalTitle>
          )}

          {isCloseable && (
            <CloseButton
              onClick={onClose}
              aria-label='Close modal'
              role='button'
              tabIndex={0}
              $skewedAngle={skewedAngle}
            />
          )}
        </ModalHeader>

        <ModalBody>{children}</ModalBody>

        {isActionable && (
          <ModalFooter $skewedAngle={skewedAngle}>
            {onConfirm && (
              <ConfirmButton onClick={onConfirm}>Confirm</ConfirmButton>
            )}

            <FooterButton onClick={onClose}>Close</FooterButton>
          </ModalFooter>
        )}
      </ModalContainer>
    </Container>,
    document.body,
  );
};

export default memo(Modal);
